import React, { useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Store, Award, Camera, Image as ImageIcon } from "lucide-react";
import { useLojas } from "@/hooks/useLojas";
import { logError } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FAIXAS = [
  ["599 a 999", "R$ 10"], ["1000 a 1399", "R$ 15"], ["1400 a 1898", "R$ 30"],
  ["1899 a 2598", "R$ 50"], ["2599 a 3300", "R$ 75"], ["3301 a 4400", "R$ 100"],
  ["4401 a 5000", "R$ 150"], ["5001 ou mais", "R$ 200"],
];
const fmt = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hoje = () => new Date().toISOString().slice(0, 10);
const inicioMes = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };

export default function PremiacaoLentes() {
  const LOJAS = useLojas();
  const [loja, setLoja] = useState(null);
  const [gateOk, setGateOk] = useState(false);
  const [inicio, setInicio] = useState(inicioMes());
  const [fim, setFim] = useState(hoje());
  const [dados, setDados] = useState([]);
  const [resumo, setResumo] = useState({ qtd_lentes: 0, total_lentes: 0, total_premio: 0 });
  const [carregando, setCarregando] = useState(false);
  const fileRef = useRef(null);
  const [uploadId, setUploadId] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = { start_date: `${inicio}T00:00:00+00:00`, end_date: `${fim}T23:59:59+00:00` };
      if (loja !== "Todas as lojas") params.loja = loja;
      const res = await axios.get(`${API}/premiacao/lentes`, { params });
      setDados(res.data.dados || []);
      setResumo(res.data.resumo || { qtd_lentes: 0, total_lentes: 0, total_premio: 0 });
    } catch (e) {
      logError(e); toast.error("Erro ao carregar premiação");
    } finally { setCarregando(false); }
  }, [inicio, fim, loja]);

  const escolherLoja = (v) => { setLoja(v); setGateOk(true); setTimeout(() => carregarComLoja(v), 0); };
  const carregarComLoja = async (v) => {
    setCarregando(true);
    try {
      const params = { start_date: `${inicio}T00:00:00+00:00`, end_date: `${fim}T23:59:59+00:00` };
      if (v !== "Todas as lojas") params.loja = v;
      const res = await axios.get(`${API}/premiacao/lentes`, { params });
      setDados(res.data.dados || []);
      setResumo(res.data.resumo || { qtd_lentes: 0, total_lentes: 0, total_premio: 0 });
    } catch (e) { logError(e); } finally { setCarregando(false); }
  };

  const abrirUpload = (receitaId) => { setUploadId(receitaId); fileRef.current?.click(); };
  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadId) return;
    const form = new FormData();
    form.append("file", file);
    try {
      await axios.post(`${API}/premiacao/foto-os/${uploadId}`, form);
      toast.success("Foto da OS anexada");
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar foto");
    }
  };

  const fmtData = (d) => d ? String(d).slice(0, 10).split("-").reverse().join("/") : "-";

  if (!gateOk) {
    const opcoes = [{ value: "Todas as lojas", label: "Gerencial (todas as lojas)", gerencial: true }, ...LOJAS.map((l) => ({ value: l, label: l }))];
    return (
      <div className="max-w-2xl mx-auto" data-testid="premiacao-loja-gate">
        <div className="text-center mb-8 mt-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 text-white mb-4"><Award className="w-7 h-7" /></div>
          <h1 className="text-3xl font-bold text-slate-900">Premiação Lentes — Selecione a loja</h1>
          <p className="text-sm text-slate-500 mt-1">Escolha uma loja ou entre no modo gerencial</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {opcoes.map((o) => (
            <button key={o.value} onClick={() => escolherLoja(o.value)}
              data-testid={`premiacao-gate-${o.value.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left hover:shadow-md ${o.gerencial ? "border-slate-900 bg-slate-900 text-white sm:col-span-2 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"}`}>
              <Store className={`w-5 h-5 shrink-0 ${o.gerencial ? "text-white" : "text-slate-500"}`} />
              <span className="font-semibold">{o.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="premiacao-page">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileSelected} data-testid="premiacao-file-input" />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><Award className="w-7 h-7 text-slate-700" /> Premiação Lentes</h1>
          <p className="text-sm text-slate-500">Somente o valor da lente vendida · {loja}</p>
        </div>
        <Button variant="outline" onClick={() => setGateOk(false)} data-testid="premiacao-trocar-loja"><Store className="w-4 h-4 mr-2" />Trocar loja</Button>
      </div>

      {/* Filtro de datas + tabela de faixas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Card className="bg-white border-slate-200 lg:col-span-2">
          <CardContent className="pt-4 flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">De</Label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} data-testid="premiacao-inicio" /></div>
            <div><Label className="text-xs">Até</Label><Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} data-testid="premiacao-fim" /></div>
            <Button onClick={carregar} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="premiacao-filtrar">Filtrar</Button>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Tabela de Premiação (valor da lente)</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
              {FAIXAS.map(([faixa, premio]) => (
                <div key={faixa} className="flex justify-between border-b border-slate-50 py-0.5"><span className="text-slate-600">{faixa}</span><span className="font-semibold text-emerald-700">{premio}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Totais */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <Card className="bg-white border-slate-200"><CardContent className="pt-4"><p className="text-xs text-slate-500 uppercase">Lentes no período</p><p className="text-2xl font-bold text-slate-900" data-testid="premiacao-qtd">{resumo.qtd_lentes}</p></CardContent></Card>
        <Card className="bg-white border-slate-200"><CardContent className="pt-4"><p className="text-xs text-slate-500 uppercase">Total em lentes</p><p className="text-2xl font-bold text-slate-900">{fmt(resumo.total_lentes)}</p></CardContent></Card>
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="pt-4"><p className="text-xs text-emerald-600 uppercase font-medium">Total a premiar (conferência)</p><p className="text-2xl font-bold text-emerald-800" data-testid="premiacao-total">{fmt(resumo.total_premio)}</p></CardContent></Card>
      </div>

      {loja === "Todas as lojas" && resumo.por_loja?.length > 0 && (
        <div className="mb-4" data-testid="premiacao-por-loja">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Total do período por loja</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {resumo.por_loja.map((l) => (
              <div key={l.loja} className="border border-slate-200 rounded-lg bg-white p-3" data-testid={`premiacao-loja-${l.loja}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-900">{l.loja}</span>
                  <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">{l.qtd} lentes</span>
                </div>
                <div className="flex justify-between text-xs text-slate-500"><span>Lentes</span><span className="font-medium text-slate-700">{fmt(l.total_lentes)}</span></div>
                <div className="flex justify-between text-sm mt-0.5"><span className="text-emerald-600">Premiação</span><span className="font-bold text-emerald-700" data-testid={`premiacao-loja-total-${l.loja}`}>{fmt(l.total_premio)}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>Data</TableHead><TableHead>Loja</TableHead><TableHead>OS</TableHead><TableHead>Cliente</TableHead>
                <TableHead className="text-right">Valor Lente</TableHead><TableHead className="text-right">Prêmio</TableHead><TableHead>Foto OS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {carregando && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Carregando...</TableCell></TableRow>}
              {!carregando && dados.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nenhuma lente no período</TableCell></TableRow>}
              {!carregando && dados.map((r) => (
                <TableRow key={r.id} data-testid={`premiacao-row-${r.id}`}>
                  <TableCell className="text-slate-600 text-xs">{fmtData(r.data_registro)}</TableCell>
                  <TableCell className="text-slate-600">{r.nome_loja || "-"}{r.is_adicional && <span className="ml-1 text-[9px] bg-blue-100 text-blue-700 px-1 rounded">extra</span>}</TableCell>
                  <TableCell className="font-mono">{r.os || "-"}</TableCell>
                  <TableCell>{r.nome_cliente || "-"}</TableCell>
                  <TableCell className="text-right font-medium">{fmt(r.valor_lente)}</TableCell>
                  <TableCell className="text-right font-bold text-emerald-700" data-testid={`premiacao-premio-${r.id}`}>{fmt(r.premio)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {r.foto_os && <a href={r.foto_os} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800" title="Ver foto da OS" data-testid={`premiacao-ver-foto-${r.id}`}><ImageIcon className="w-4 h-4" /></a>}
                      <button onClick={() => abrirUpload(r.receita_id)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700" data-testid={`premiacao-upload-${r.id}`} title="Anexar foto da OS">
                        <Camera className="w-3.5 h-3.5" />{r.foto_os ? "Trocar" : "Anexar"}
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
